<template>
  <v-layout
    row
    justify-center
  >
    <v-dialog
      v-model="isOpen"
      persistent
      max-width="1000px"
    >
      <v-card class="create-bridge-signing">
        <v-form>
          <v-card-title class="toolbar primary white--text">
            <v-icon dark>
              note_add
            </v-icon>
            <h3
              class="title ml-2"
            >
              {{ !item.id ? 'Create' : 'Update' }} Legal Entity
            </h3>
            <v-spacer />
            <v-btn
              color="white"
              flat
              @click="onToggleOpen"
            >
              <v-icon>
                disabled_by_default
              </v-icon>
            </v-btn>
          </v-card-title>
          <v-progress-linear
            v-if="isLoading"
            :indeterminate="true"
            class="ma-0"
          />

          <v-card-text>
            <v-container
              grid-list-md
              class="pa-0"
            >
              <v-layout
                wrap
                class="form-group"
              >
                <v-flex xs12>
                  <h6 class="subheading">
                    Legal Entity
                  </h6>
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.id"
                    label="ID"
                    :disabled="!!item.id"
                    :error-messages="getFieldErrors('id')"
                    @change="$v.formData.id.$touch()"
                    @blur="$v.formData.id.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.company_name"
                    label="Company Name"
                    :error-messages="getFieldErrors('company_name')"
                    @change="$v.formData.company_name.$touch()"
                    @blur="$v.formData.company_name.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.company_type"
                    label="Company Type"
                    :error-messages="getFieldErrors('company_type')"
                    @change="$v.formData.company_type.$touch()"
                    @blur="$v.formData.company_type.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.country"
                    label="Country"
                    :error-messages="getFieldErrors('country')"
                    @change="$v.formData.country.$touch()"
                    @blur="$v.formData.country.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.postal_code"
                    label="Postal Code"
                    :error-messages="getFieldErrors('postal_code')"
                    @change="$v.formData.postal_code.$touch()"
                    @blur="$v.formData.postal_code.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.city"
                    label="City"
                    :error-messages="getFieldErrors('city')"
                    @change="$v.formData.city.$touch()"
                    @blur="$v.formData.city.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.street"
                    label="Street"
                    :error-messages="getFieldErrors('street')"
                    @change="$v.formData.street.$touch()"
                    @blur="$v.formData.street.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.street_no"
                    label="Street Number"
                    :error-messages="getFieldErrors('street_no')"
                    @change="$v.formData.street_no.$touch()"
                    @blur="$v.formData.street_no.$touch()"
                  />
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="secondary"
              flat
              @click="onToggleOpen"
            >
              Cancel
            </v-btn>
            <v-btn
              :disabled="isLoading || !$v.formData.$anyDirty"
              :loading="isLoading"
              color="primary"
              @click="submit"
            >
              <span>
                {{ !item.id ? 'Create' : 'Update' }} Legal Entity
              </span>
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import { maxLength, minLength, required } from 'vuelidate/lib/validators';

export default {
  name: 'CreateDialog',
  props: {
    item: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
    toggleOpen: {
      type: Function,
      required: true,
    },
  },
  data: () => ({
    isOpenProp: this.isOpen,
    isLoading: false,
    error: null,
    formData: {
      id: null,
      city: '',
      company_name: '',
      company_type: '',
      country: '',
      postal_code: '',
      street: '',
      street_no: '',
    },
  }),
  validations() {
    return {
      formData: {
        id: { required },
        city: { required },
        company_name: { required },
        company_type: {},
        country: { required, minLength: minLength(3), maxLength: maxLength(3) },
        postal_code: { required },
        street: { required },
        street_no: { required },
      },
    };
  },
  computed: {
    validFields() {
      return Object.keys(this.$v.formData.$params).filter(
        item => !this.$v.formData[item].$invalid,
      );
    },
    errors() {
      return Object.keys(this.$v.formData.$params).filter(
        item => this.$v.formData[item].$invalid,
      );
    },
  },
  mounted() {
    this.formData = {
      ...this.formData,
      ...(this.serializeItem(this.item)),
    };

    this.$v.$touch();
  },
  methods: {
    getFieldErrors(field) {
      const errors = [];
      const formField = this.getProperty(this.$v.formData, field);

      if (formField) {
        if (!formField.$dirty || !formField.$invalid) {
          return errors;
        }

        if (!formField.required) {
          errors.push('Required field');
        }

        if (!formField.minLength) {
          errors.push('Field has a minimum length');
        }

        if (!formField.maxLength) {
          errors.push('Field has a maximum length');
        }
      }

      return errors;
    },
    getProperty(object, propString) {
      if (!propString) {
        return object;
      }

      let prop;
      const props = propString.split('.');

      let i;
      const iLen = props.length - 1;
      // eslint-disable-next-line no-plusplus
      for (i = 0; i < iLen; i++) {
        prop = props[i];
        const candidate = object[prop];
        if (candidate !== undefined) {
          object = candidate;
        } else {
          break;
        }
      }

      return object[props[i]];
    },
    onToggleOpen() {
      this.toggleOpen();
      this.error = null;
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      this.isLoading = true;
      if (this.item.id) {
        this.$store
          .dispatch('updatePatLegalEntity', this.serializeFormData(this.formData))
          .then(() => {
            this.onToggleOpen();
          })
          .catch((err) => {
            this.error = err;
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else {
        this.$store
          .dispatch('createPatLegalEntity', this.serializeFormData(this.formData))
          .then(() => {
            this.onToggleOpen();
          })
          .catch((err) => {
            this.error = err;
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    serializeItem(item) {
      const { ...serializeItem } = item;

      return {
        ...serializeItem,
      };
    },
    serializeFormData(formData) {
      const { ...serializeFormData } = formData;

      serializeFormData.company_type = serializeFormData.company_type || '';

      return {
        ...serializeFormData,
      };
    },
  },
};
</script>

<style lang="scss">
.create-bridge-signing {

  .v-card__text {
    max-height: 65vh;
    overflow: auto;
    padding-top: 32px;
  }

  .v-card__actions {
    border-top: 1px solid rgba(0, 0, 0, 0.12);
  }

  .subheading {
    padding: 8px;
    background: white;
    color: rgba(0, 0, 0, 0.54);
    top: -20px;
    left: 8px;
    position: absolute;
    display: flex;
    align-items: center;
  }

  .form-group {
    padding: 16px;
    margin: 0 0 24px 0;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 5px;
    position: relative;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/payoutsAndTaxes/payout/dialogs/legalEntity/CreateDialog.vue